import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import type { CreateFirewallRulesetRequest, FirewallRulesetSummary } from '@nadoutong/contracts';
import { firewallIssuesFromError } from '../api/firewallRulesets.ts';
import {
  useCreateFirewallRuleset,
  useDeleteFirewallRuleset,
  useFirewallRulesets,
} from '../api/useFirewallRulesets.ts';
import { FirewallRulesetCreateDialog } from '../components/FirewallRulesetCreateDialog.tsx';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  IconButton,
  PageHeader,
  Table,
  TableEmpty,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * Firewall ruleset management (#1760).
 *
 * A SEPARATE surface from "which ruleset does this binding reference", which
 * lives in the egress editor. The split is not organisational tidiness: editing
 * one binding's reference and editing a ruleset every binding references have
 * completely different blast radii, and mixing them in one screen is how an
 * operator edits the shared thing while believing they edited the local one.
 *
 * Nothing on this page changes live traffic except activation, which is on the
 * detail page behind its own confirmation.
 */

function ownerLabel(ruleset: FirewallRulesetSummary): string | null {
  if (ruleset.owner_key_bucket_id === null || ruleset.owner_channel_id === null) return null;
  return `${ruleset.owner_key_bucket_id} · ${ruleset.owner_channel_id}`;
}

export function FirewallRulesetsPage(): JSX.Element {
  const t = useT();
  // Two queries because the list endpoint filters by exactly one scope and
  // defaults to `shared`. Asking once would silently hide every binding-scoped
  // ruleset — including one just created from this page's own dialog, which
  // offers `binding` as a choice.
  const sharedQ = useFirewallRulesets({ scope: 'shared' });
  const bindingQ = useFirewallRulesets({ scope: 'binding' });
  const createRuleset = useCreateFirewallRuleset();
  const deleteRuleset = useDeleteFirewallRuleset();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FirewallRulesetSummary | null>(null);

  const createIssues = firewallIssuesFromError(createRuleset.error);

  function handleCreate(body: CreateFirewallRulesetRequest): void {
    createRuleset.mutate(body, {
      onSuccess: () => {
        setCreateOpen(false);
        toast.success(t('firewall_rulesets.toast.created'));
      },
      onError: (error) => {
        // The per-issue detail renders inside the dialog, which stays open.
        // The toast carries the envelope message so a failure that has no
        // issue list (auth, gateway down) is not silent.
        if (firewallIssuesFromError(error).length === 0) {
          toast.error(coerceErrorMessage(error));
        }
      },
    });
  }

  function remove(ruleset: FirewallRulesetSummary): void {
    deleteRuleset.mutate(ruleset.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        toast.success(t('firewall_rulesets.toast.deleted'));
      },
      onError: (error) => toast.error(coerceErrorMessage(error)),
    });
  }

  const rows = [...(sharedQ.data ?? []), ...(bindingQ.data ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name));
  const listLoading = sharedQ.isLoading || bindingQ.isLoading;
  // Either half failing means the table is incomplete, and an incomplete list
  // of live policy is worse than no list: it reads as "this ruleset does not
  // exist".
  const listError = sharedQ.isError ? sharedQ.error : bindingQ.isError ? bindingQ.error : null;

  return (
    <section className="flex flex-col gap-6" data-testid="firewall-rulesets-page">
      <PageHeader
        title={t('firewall_rulesets.title')}
        sub={t('firewall_rulesets.subtitle')}
        actions={
          <Button
            variant="primary"
            onClick={() => setCreateOpen(true)}
            data-testid="firewall-ruleset-create-open"
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            <span>{t('firewall_rulesets.create_btn')}</span>
          </Button>
        }
      />

      <section
        className="flex flex-col gap-1 rounded-lg border border-line-soft bg-card p-4"
        data-testid="firewall-rulesets-explainer"
      >
        <h2 className="m-0 text-section text-paper">{t('firewall_rulesets.explain.title')}</h2>
        <p className="m-0 text-dense text-paper-low">{t('firewall_rulesets.explain.online')}</p>
        <p className="m-0 text-dense text-paper-low">{t('firewall_rulesets.explain.frozen')}</p>
        <p className="m-0 text-dense text-paper-low">{t('firewall_rulesets.explain.order')}</p>
      </section>

      {listLoading ? (
        <div className="h-40 animate-pulse rounded-lg bg-well" />
      ) : listError !== null ? (
        <p className="m-0 text-body text-bad" role="alert">
          {coerceErrorMessage(listError)}
        </p>
      ) : (
        <Table className="min-w-0" testId="firewall-rulesets-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('firewall_rulesets.col.name')}</Th>
              <Th>{t('firewall_rulesets.col.scope')}</Th>
              <Th>{t('firewall_rulesets.col.active')}</Th>
              <Th>{t('firewall_rulesets.col.latest')}</Th>
              <Th>{t('firewall_rulesets.col.updated')}</Th>
              <Th className="text-right">{t('firewall_rulesets.col.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.length === 0 ? (
              <TableEmpty colSpan={6}>{t('firewall_rulesets.empty')}</TableEmpty>
            ) : rows.map((ruleset) => {
              const owner = ownerLabel(ruleset);
              const hasUnactivatedDraft = ruleset.latest_version !== null
                && ruleset.latest_version !== ruleset.active_version;
              return (
                <Tr key={ruleset.id}>
                  <Td>
                    <div className="flex flex-col gap-1">
                      <Link
                        to={`/supply/firewall-rulesets/${encodeURIComponent(ruleset.id)}`}
                        className="text-dense font-medium text-paper hover:text-signal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
                        data-testid={`firewall-ruleset-link-${ruleset.id}`}
                      >
                        {ruleset.name}
                      </Link>
                      <span className="font-mono text-label text-paper-low">{ruleset.id}</span>
                    </div>
                  </Td>
                  <Td>
                    <div className="flex flex-col gap-1">
                      <Chip tone={ruleset.scope === 'shared' ? 'signal' : 'neutral'} lamp={false}>
                        {t(`firewall_rulesets.scope.${ruleset.scope}`)}
                      </Chip>
                      {owner === null ? null : (
                        <span className="font-mono text-micro text-paper-low">{owner}</span>
                      )}
                    </div>
                  </Td>
                  <Td>
                    {ruleset.active_version === null ? (
                      <Chip tone="neutral" lamp={false}>
                        {t('firewall_rulesets.status.unarmed')}
                      </Chip>
                    ) : (
                      <Chip tone="ok">
                        {t('firewall_rulesets.status.active', { version: ruleset.active_version })}
                      </Chip>
                    )}
                  </Td>
                  <Td>
                    <div className="flex flex-col gap-1">
                      <span className="font-mono text-label text-paper-mid">
                        {ruleset.latest_version === null ? '—' : `v${String(ruleset.latest_version)}`}
                      </span>
                      {hasUnactivatedDraft ? (
                        <span
                          className="text-micro text-warn"
                          data-testid={`firewall-ruleset-draft-${ruleset.id}`}
                        >
                          {t('firewall_rulesets.status.draft_pending')}
                        </span>
                      ) : null}
                    </div>
                  </Td>
                  <Td className="whitespace-nowrap font-mono text-label text-paper-low">
                    {formatLocalTimestamp(ruleset.updated_at, { seconds: true })}
                  </Td>
                  <Td className="whitespace-nowrap text-right">
                    <IconButton
                      size="sm"
                      label={t('firewall_rulesets.action.delete')}
                      onClick={() => setDeleteTarget(ruleset)}
                      disabled={deleteRuleset.isPending}
                      className="hover:bg-bad-dim hover:text-bad"
                      data-testid={`firewall-ruleset-delete-${ruleset.id}`}
                    >
                      <Trash2 aria-hidden="true" className="h-4 w-4" />
                    </IconButton>
                  </Td>
                </Tr>
              );
            })}
          </TBody>
        </Table>
      )}

      {createOpen ? (
        <FirewallRulesetCreateDialog
          pending={createRuleset.isPending}
          serverIssues={createIssues}
          onClose={() => setCreateOpen(false)}
          onSubmit={handleCreate}
        />
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next && !deleteRuleset.isPending) setDeleteTarget(null);
        }}
        title={t('firewall_rulesets.delete.title')}
        body={deleteTarget === null
          ? undefined
          : t('firewall_rulesets.delete.body', { name: deleteTarget.name })}
        tone="danger"
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={deleteRuleset.isPending}
        onConfirm={() => {
          if (deleteTarget !== null) remove(deleteTarget);
        }}
        testId="firewall-ruleset-delete-confirm"
      />
    </section>
  );
}
